import React, { useMemo } from "react";
import { roundedPanelSx } from "../../overviewPanel";
import { Box, Typography, Skeleton, useTheme } from "@mui/material";
import { ResponsiveBar, BarDatum } from "@nivo/bar";
import ChartTooltipPortal from "./shared/ChartTooltipPortal";
import { useGradesConsensus } from "../hooks/useGradesConsensus";

interface AnalystGradesChartProps {
  ticker: string;
}

// Grade configuration with semantic colors (bullish → bearish)
const GRADE_CONFIG = {
  strongBuy: { label: "Strong Buy", color: "#16a34a", order: 0 },
  buy: { label: "Buy", color: "#22c55e", order: 1 },
  hold: { label: "Hold", color: "#eab308", order: 2 },
  sell: { label: "Sell", color: "#f97316", order: 3 },
  strongSell: { label: "Strong Sell", color: "#ef4444", order: 4 },
} as const;

type GradeKey = keyof typeof GRADE_CONFIG;

export const AnalystGradesChart: React.FC<AnalystGradesChartProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { data, isLoading, error } = useGradesConsensus(ticker);

  // Transform data for Nivo horizontal bar chart - each grade as separate bar
  const chartData = useMemo((): BarDatum[] => {
    if (!data) return [];

    const grades: { key: GradeKey; value: number }[] = [
      { key: "strongBuy", value: data.strongBuy },
      { key: "buy", value: data.buy },
      { key: "hold", value: data.hold },
      { key: "sell", value: data.sell },
      { key: "strongSell", value: data.strongSell },
    ];

    // Return in reverse order so Strong Buy appears at top
    return grades.reverse().map(({ key, value }) => ({
      grade: GRADE_CONFIG[key].label,
      count: value,
      color: GRADE_CONFIG[key].color,
    }));
  }, [data]);

  const total = useMemo(() => {
    if (!data) return 0;
    return data.strongBuy + data.buy + data.hold + data.sell + data.strongSell;
  }, [data]);

  const maxValue = useMemo(() => {
    if (!data) return 0;
    return Math.max(
      data.strongBuy,
      data.buy,
      data.hold,
      data.sell,
      data.strongSell
    );
  }, [data]);

  // Determine consensus badge color
  const getConsensusColor = (consensus: string) => {
    const c = consensus.toLowerCase();
    if (c.includes("strong buy")) return "#16a34a";
    if (c.includes("buy")) return "#22c55e";
    if (c.includes("hold")) return "#eab308";
    if (c.includes("strong sell")) return "#ef4444";
    if (c.includes("sell")) return "#f97316";
    return theme.palette.text.secondary;
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Skeleton variant="text" width={120} height={16} />
          <Skeleton variant="rounded" width={60} height={24} />
        </Box>
        {[1, 2, 3, 4, 5].map((i) => (
          <Box
            key={i}
            sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}
          >
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton
              variant="rectangular"
              height={24}
              sx={{ flex: 1, borderRadius: 0.5 }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  // Error or no data state
  if (error || !data) {
    return (
      <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            display: "block",
            mb: 2,
          }}
        >
          Analyst Grades
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          Analyst grades data not available for this stock
        </Typography>
      </Box>
    );
  }

  const consensusColor = getConsensusColor(data.consensus);

  return (
    <Box sx={roundedPanelSx(theme.palette.mode === "dark")}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{
              color: theme.palette.text.primary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Analyst Grades
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: theme.palette.text.secondary,
            }}
          >
            {total} ratings
          </Typography>
        </Box>

        {/* Consensus Badge */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: `${consensusColor}15`,
            border: `1px solid ${consensusColor}30`,
            borderRadius: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: consensusColor,
              letterSpacing: "0.02em",
            }}
          >
            {data.consensus}
          </Typography>
        </Box>
      </Box>

      {/* Nivo Horizontal Bar Chart */}
      <Box sx={{ height: 165 }}>
        <ResponsiveBar
          data={chartData}
          keys={["count"]}
          indexBy="grade"
          layout="horizontal"
          margin={{ top: 0, right: 50, bottom: 0, left: 90 }}
          padding={0.35}
          valueScale={{ type: "linear", max: maxValue > 0 ? Math.ceil(maxValue * 1.15) : 10 }}
          colors={(bar) => bar.data.color as string}
          borderRadius={3}
          borderWidth={0}
          enableGridX={false}
          enableGridY={false}
          axisTop={null}
          axisRight={null}
          axisBottom={null}
          axisLeft={{
            tickSize: 0,
            tickPadding: 12,
            tickRotation: 0,
          }}
          enableLabel={true}
          label={(d) => `${d.value}`}
          labelSkipWidth={24}
          labelSkipHeight={0}
          labelTextColor="#ffffff"
          isInteractive={true}
          tooltip={({ indexValue, value, color }) => (
            <ChartTooltipPortal>
              <Box
                sx={{
                  backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: color,
                    borderRadius: 0.5,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {indexValue}:{" "}
                  <span style={{ fontWeight: 700 }}>{value} analysts</span>
                </Typography>
              </Box>
            </ChartTooltipPortal>
          )}
          role="img"
          ariaLabel="Analyst grades distribution chart"
          theme={{
            axis: {
              ticks: {
                text: {
                  fontSize: 13,
                  fontWeight: 600,
                  fill: theme.palette.text.secondary,
                },
              },
            },
            labels: {
              text: {
                fontSize: 12,
                fontWeight: 600,
              },
            },
          }}
        />
      </Box>

    </Box>
  );
};

export default AnalystGradesChart;
